package er.prateek.crossly.ui.components

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.Depth
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith

enum class GameButtonStyle { PRIMARY, SECONDARY, GHOST, DANGER, GOLD }

enum class GameButtonSize { SMALL, MEDIUM, LARGE }

/**
 * The app's one pressable surface: a "sticker" button with a solid shadow slab
 * beneath its face. Pressing travels the face down onto the slab instead of
 * running a Material ripple, which is what makes taps feel physical on a
 * touchscreen and equally deliberate under a mouse.
 *
 * Hover is handled too (desktop and web are first-class targets here), and the
 * whole thing is driven from [er.prateek.crossly.ui.theme.GameColors] so every
 * palette restyles it for free.
 */
@Composable
fun GameButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    style: GameButtonStyle = GameButtonStyle.PRIMARY,
    size: GameButtonSize = GameButtonSize.MEDIUM,
    enabled: Boolean = true,
    fillWidth: Boolean = false,
    fillHeight: Boolean = false,
    icon: (@Composable (Color) -> Unit)? = null,
    /** Small second line under [text] -- e.g. an "Ad" tag on an ad-gated action. */
    caption: String? = null,
) {
    GameButtonSurface(
        onClick = onClick,
        modifier = modifier,
        style = style,
        size = size,
        enabled = enabled,
        fillWidth = fillWidth,
        fillHeight = fillHeight,
        // The face carries the sizing, not just the parent: the slab matches the
        // parent, so a face left wrapping its label leaves the slab sticking out.
        faceModifier = Modifier
            .then(if (fillWidth) Modifier.fillMaxWidth() else Modifier)
            .then(if (fillHeight) Modifier.fillMaxHeight() else Modifier),
    ) { contentColor ->
        icon?.invoke(contentColor)
        if (caption == null) {
            Text(
                text = text,
                color = contentColor,
                style = when (size) {
                    GameButtonSize.SMALL -> MaterialTheme.typography.labelMedium
                    GameButtonSize.MEDIUM -> MaterialTheme.typography.labelLarge
                    GameButtonSize.LARGE -> MaterialTheme.typography.titleLarge
                },
                textAlign = TextAlign.Center,
            )
        } else {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    text = text,
                    color = contentColor,
                    style = when (size) {
                        GameButtonSize.SMALL -> MaterialTheme.typography.labelMedium
                        GameButtonSize.MEDIUM -> MaterialTheme.typography.labelLarge
                        GameButtonSize.LARGE -> MaterialTheme.typography.titleLarge
                    },
                    textAlign = TextAlign.Center,
                )
                Text(
                    text = caption,
                    color = contentColor.copy(alpha = 0.75f),
                    style = MaterialTheme.typography.labelSmall,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

/** Square icon-only variant — top-bar back/settings affordances. */
@Composable
fun GameIconButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    style: GameButtonStyle = GameButtonStyle.SECONDARY,
    enabled: Boolean = true,
    diameter: Dp = 46.dp,
    icon: @Composable (Color) -> Unit,
) {
    GameButtonSurface(
        onClick = onClick,
        modifier = modifier,
        style = style,
        size = GameButtonSize.MEDIUM,
        enabled = enabled,
        fillWidth = false,
        // The face carries the square size: the slab matches the parent, so
        // sizing only the parent would leave the face wrapped around the icon.
        faceModifier = Modifier.size(diameter),
        shape = RoundedCornerShape(16.dp),
        contentPadding = PaddingValues(0.dp),
        content = { contentColor -> icon(contentColor) },
    )
}

@Composable
private fun GameButtonSurface(
    onClick: () -> Unit,
    modifier: Modifier,
    style: GameButtonStyle,
    size: GameButtonSize,
    enabled: Boolean,
    fillWidth: Boolean,
    fillHeight: Boolean = false,
    faceModifier: Modifier = Modifier,
    shape: Shape = RoundedCornerShape(Radius.button),
    contentPadding: PaddingValues = when (size) {
        GameButtonSize.SMALL -> PaddingValues(horizontal = Spacing.md, vertical = Spacing.sm)
        GameButtonSize.MEDIUM -> PaddingValues(horizontal = Spacing.xl, vertical = 14.dp)
        GameButtonSize.LARGE -> PaddingValues(horizontal = Spacing.xl, vertical = 20.dp)
    },
    content: @Composable RowScope.(Color) -> Unit,
) {
    val colors = MaterialTheme.game
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val hovered by interactionSource.collectIsHoveredAsState()

    val depth = if (size == GameButtonSize.SMALL) Depth.buttonSmall else Depth.button
    val restingDepth = if (enabled) depth else 0.dp
    // The face drops onto the slab on press; hover lifts it a hair.
    val travel by animateDpAsState(
        targetValue = when {
            !enabled -> restingDepth
            pressed -> restingDepth
            hovered -> 0.dp - 1.dp
            else -> 0.dp
        },
        animationSpec = spring(dampingRatio = 0.55f, stiffness = 1400f),
        label = "buttonTravel",
    )

    val face: Brush
    val contentColor: Color
    val border: BorderStroke?
    when (style) {
        GameButtonStyle.PRIMARY -> {
            face = colors.accentBrush
            contentColor = colors.onAccent
            border = null
        }
        GameButtonStyle.GOLD -> {
            face = colors.goldBrush
            contentColor = Color(0xFF3A2600)
            border = null
        }
        GameButtonStyle.SECONDARY -> {
            face = Brush.verticalGradient(listOf(colors.card, colors.cardElevated))
            contentColor = colors.ink
            border = BorderStroke(1.5.dp, colors.cardBorder)
        }
        GameButtonStyle.GHOST -> {
            face = Brush.verticalGradient(listOf(Color.Transparent, Color.Transparent))
            contentColor = colors.inkMuted
            border = BorderStroke(1.5.dp, colors.cardBorder)
        }
        GameButtonStyle.DANGER -> {
            face = Brush.verticalGradient(listOf(colors.danger, colors.danger.mixWith(colors.flame, 0.35f)))
            contentColor = Color.White
            border = null
        }
    }

    val slabColor = when (style) {
        GameButtonStyle.PRIMARY -> colors.accent.mixWith(Color.Black, 0.35f)
        GameButtonStyle.GOLD -> colors.gold.mixWith(Color.Black, 0.35f)
        GameButtonStyle.DANGER -> colors.danger.mixWith(Color.Black, 0.35f)
        GameButtonStyle.SECONDARY, GameButtonStyle.GHOST -> colors.shadow
    }

    val alpha = if (enabled) 1f else 0.45f

    Box(
        modifier = modifier
            .then(if (fillWidth) Modifier.fillMaxWidth() else Modifier)
            .then(if (fillHeight) Modifier.fillMaxHeight() else Modifier)
            // Reserve the slab's height so a pressing button never reflows its row.
            .padding(bottom = depth),
    ) {
        if (restingDepth > 0.dp && style != GameButtonStyle.GHOST) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .offset(y = depth)
                    .clip(shape)
                    .background(slabColor.copy(alpha = alpha)),
            )
        }
        Row(
            modifier = faceModifier
                .offset(y = travel)
                .clip(shape)
                .background(face, alpha = alpha)
                .then(if (border != null) Modifier.border(border, shape) else Modifier)
                .hoverable(interactionSource, enabled = enabled)
                .clickable(
                    interactionSource = interactionSource,
                    // The travel animation *is* the press feedback; a ripple on top
                    // reads as a different design language.
                    indication = null,
                    enabled = enabled,
                    onClick = onClick,
                )
                .padding(contentPadding),
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm, Alignment.CenterHorizontally),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            content(if (enabled) contentColor else contentColor.copy(alpha = 0.7f))
        }
    }
}
